import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  // Make overflowMenu ref optional to allow for flexible HTML structures
  requiredRefs = [];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('click', this.#clickHandler);
    this.addEventListener('pointerover', this.activate);
    this.addEventListener('pointerleave', this.deactivate);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    
    // Initialize menu style and handle overflow on load
    onDocumentLoaded(() => {
      setHeaderMenuStyle();
      this.#checkOverflow();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.#clickHandler);
    this.removeEventListener('pointerover', this.activate);
    this.removeEventListener('pointerleave', this.deactivate);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#checkOverflow();
  }, 100);

  /**
   * Check if menu items overflow and dispatch event to header-component
   */
  #checkOverflow = () => {
    const menuList = this.querySelector('ul, .menu-list');
    if (!menuList) return;

    const menuWidth = menuList.scrollWidth;
    const containerWidth = menuList.clientWidth;
    const minimumReached = menuWidth > containerWidth;
    
    // Dispatch event to header-component
    this.dispatchEvent(new CustomEvent('overflowMinimum', {
      bubbles: true,
      composed: true,
      detail: { minimumReached }
    }));
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #clickHandler = (event) => {
    const item = findMenuItem(event.target);
    if (!item) return;

    // Prevent default if it's a dropdown toggle (has submenu or is "More")
    const slottedElement = item.closest('[slot]');
    const isMoreTrigger = item.textContent.trim().toLowerCase() === 'more' || (slottedElement && slottedElement.slot === 'more');
    const hasSubmenu = Boolean(findSubmenu(item));

    if (hasSubmenu || isMoreTrigger) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item === this.#state.activeItem) {
      this.#deactivate();
    } else {
      this.activate(event);
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    const ref = this.refs.overflowMenu || 
                this.querySelector('overflow-list') || 
                this.querySelector('[slot="overflow"]');
    if (!ref) return null;
    
    // Dawn uses overflow-list.overflow-menu directly as the panel
    if (ref.classList.contains('overflow-menu') || ref.tagName.toLowerCase() === 'overflow-list') {
      return ref instanceof HTMLElement ? ref : null;
    }
    
    return /** @type {HTMLElement | null} */ (
      ref.shadowRoot?.querySelector('[part="overflow"]') || 
      ref.querySelector('[part="overflow"]') || 
      ref.querySelector('.overflow-menu') || 
      ref.querySelector('.header__submenu') || 
      ref
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    const ref = this.refs.overflowMenu || 
                this.querySelector('overflow-list') || 
                this.querySelector('[slot="overflow"]');
    if (!ref) return false;
    return (
      ref.matches(':hover') || 
      ref.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') || 
      ref.querySelector('[part="overflow-list"]')?.matches(':hover') || 
      ref.querySelector('.overflow-menu')?.matches(':hover') || 
      ref.querySelector('.list-menu')?.matches(':hover')
    ) ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item || (event.type !== 'click' && item == this.#state.activeItem)) return;

    // ── More / overflow trigger detection (broadened) ──────────────────
    const slottedElement = item.closest('[slot]');
    const itemText = item.textContent?.trim().toLowerCase() ?? '';
    const isMoreTrigger =
      itemText === 'more' ||
      (slottedElement && slottedElement.slot === 'more') ||
      item.closest('[slot="overflow"]') !== null ||
      item.closest('overflow-list') !== null ||
      item.closest('.disclosure') !== null ||
      item.hasAttribute('data-overflow') ||
      item.closest('[data-overflow]') !== null;

    const isDefaultSlot = !isMoreTrigger && (!slottedElement || slottedElement.slot === '');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    // If it's a plain link with no submenu, close any open menu and bail
    if (!hasSubmenu && isDefaultSlot) {
      this.#deactivate();
      return;
    }

    this.dataset.overflowExpanded = isMoreTrigger.toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Open the <details> element if item is a <summary>
    const details = item.closest('details');
    if (details) details.open = true;

    // For More trigger: fall back to overflowMenu
    if (!hasSubmenu && isMoreTrigger) {
      submenu = this.overflowMenu;

      // Also try to open the disclosure <details> directly
      const disclosureDetails =
        item.closest('details') ||
        item.closest('.disclosure')?.querySelector('details') ||
        item.closest('[slot="overflow"]')?.closest('details');
      if (disclosureDetails) disclosureDetails.open = true;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
      setTimeout(() => this.#cleanupMutationObserver(), 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (isMoreTrigger) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) finalHeight = 0;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // ✅ FIX: Close details element on deactivation
    const details = item.closest('details');
    if (details) {
      details.open = false;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // Handle "More" slot trigger
  if (element?.matches('[slot="more"]') || element?.closest('[slot="more"]')) {
    const moreItem = element?.matches('[slot="more"]') ? element : element.closest('[slot="more"]');
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]')) || moreItem;
  }

  // Dawn's overflow-list pattern — the overflow-list element itself IS the trigger
  const overflowList = element.closest('overflow-list');
  if (overflowList) {
    // If clicking an item INSIDE the open overflow list, treat as regular link
    const insideOpenMenu = overflowList.classList.contains('overflow-menu') && 
                           element.closest('.menu-list__list-item');
    if (insideOpenMenu) {
      return element.closest('.menu-list__list-item')?.querySelector('a') instanceof HTMLElement 
        ? element.closest('.menu-list__list-item').querySelector('a') 
        : null;
    }
    return overflowList instanceof HTMLElement ? overflowList : null;
  }

  // Disclosure summary (More button rendered as <details><summary>More</summary>)
  const disclosureSummary = element.closest('.disclosure > summary') || 
                            element.closest('details > summary[aria-expanded]');
  if (disclosureSummary) return disclosureSummary instanceof HTMLElement ? disclosureSummary : null;

  return (
    element.closest('[ref="menuitem"]') ||
    element.querySelector('[ref="menuitem"]') ||
    element.closest('.header__menu-item') ||
    element.closest('.menu-list__link') ||
    null
  );
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  // For overflow-list elements, the element itself is the submenu container
  if (element?.tagName?.toLowerCase() === 'overflow-list') {
    return element instanceof HTMLElement ? element : null;
  }
  
  const submenu =
    element?.parentElement?.querySelector('[ref="submenu[]"]') ||
    element?.parentElement?.querySelector('.header__submenu') ||
    element?.parentElement?.querySelector('overflow-list.overflow-menu') ||
    element?.closest('details')?.querySelector('ul, [part="overflow"], .overflow-menu') ||
    element?.parentElement?.querySelector('ul');
  return submenu instanceof HTMLElement ? submenu : null;
}